<template>
		<div class="home">
				<home-hero :content="hero" />
				<main class="main-content">
					<div v-for="section in sections">
						<home-services v-if="section._type === 'services'" :content="section" />
						<featured-projects v-else-if="section._type === 'projectsReference'" :content="section" />
						<recent-news v-else-if="section._type === 'recentNews'" :content="theNewsletters" :text="section" />
						<home-clients v-else-if="section._type === 'clients'" :content="section" />
						<home-priority-feature v-else-if="section._type === 'priorityFeature'" :content="section" />
						<home-section v-else :content="section" />
					</div>
				</main>
		</div>
</template>

<script setup>
import { groq } from '@nuxtjs/sanity/runtime/groq'
import {useHead} from 'nuxt/app'
import { useI18n } from '#imports'
const { locale } = useI18n()
import {computed} from 'vue'
const query = groq`{
'home': *[_type == 'home' && title == 'Home']{
  title,
  metaTitle,
  metaDescription,
  hero {
    title,
    subtitle,
    singleVideo {
      videoType,
      videoId,
      title,
      isLooping,
      isAutoplay,
      hasControls
    }
  },
  sections[]->{
  	...,
  	isTopBlock,
  	title,
  	extraTitle,
  	isDarkMode,
  	alignment,
  	text,
  	buttonText,
  	button->{
      slug,
      text
    },
    features[]->{
			title,
			subheading,
			text,
			slug,
			isDarkMode,
			image{
				alt,
				image{
					asset->{
						metadata{
							dimensions{
								height,
								width
							}
						},
						url
					},
				}
			}
		},
    image{
			asset->{
				metadata{
					dimensions{
						height,
						width
					}
				},
				url
			}
    },
    alt,
    columnRatio,
    singleVideo{
      videoType,
      title,
      isAutoplay,
      isLooping,
      hasControls,
      videoId,
      video
    },
    logos[]{
    	logo{
				asset->{
					metadata{
						dimensions{
							height,
							width
						}
					},
				url
				}
    	},
    	name
    },
    logosFirstRow[]{
      logo{
        asset->{
          metadata{
            dimensions{
              height,
              width
            }
          },
          url
        }
      },
      name,
      slug
    },
    logosSecondRow[]{
      logo{
        asset->{
          metadata{
            dimensions{
              height,
              width
            }
          },
          url
        }
      },
      name,
      slug
    },
		services[]->{
			title,
			slug,
			links,
			hero{
			 asset->{
				 metadata{
					dimensions{
						height,
						width
						}
					},
					url
				 }
			},
			heroAlt
		},
		projects[]->{
      title,
      slug,
      projectType,
      projectHero{
       asset->{
       metadata{
          dimensions{
            height,
            width
          }
        },
        url
       }
      },
      projectHeroAlt
    },
    linkType,
    projectsLinkText,
    newsLinkText
  }
}[0],
 'newsletters': *[_type == 'newsLetter'] | order(publishedAt desc) {
    title,
    slug,
    thumb{
      asset->{
        metadata{
          dimensions{
            height,
            width
          }
        },
        url
      }
    },
    thumbAlt,
    thumbAlignment,
    publishedAt,
    _createdAt
  }[0...5]
}`;
const sanity = useSanity()
const { data } = await useAsyncData('page:home', () => sanity.fetch(query))
const htmlLang = computed(() => locale.value === 'en' ? 'en-US' : 'ja-JA')
const home = computed(() => data.value?.home ?? {})
const headTitle = computed(() => home.value.metaTitle ?? null)
const description = computed(() => home.value.metaDescription ?? null)
const hero = computed(() => home.value.hero ?? {})
const sections = computed(() => home.value.sections ?? [])
const theNewsletters = computed(() => data.value?.newsletters ?? [])
const processedTitle = computed(() => locale.value === 'en' ? headTitle.value?.en : headTitle.value?.jp )
const processedDescription = computed(() => locale.value === 'en' ? description.value?.en : description.value?.jp )
useHead({
	htmlAttrs: {
		lang: htmlLang
	},
	title: computed(() => processedTitle.value || 'TokyoDex'),
	viewport: 'width=device-width, initial-scale=1, maximum-scale=1',
	charset: 'utf-8',
	meta: [
		{ name: 'description', content: computed(() => processedDescription.value || '') }
	],
})
</script>

<style lang="scss">
  .section__heading--projects {
    margin: 0 0 1.5rem 0;
  }
</style>
